// frontend/src/pages/HuntersMatches.jsx
//
// Vue chasseur : signalements de dégâts ouverts à proximité.
// MVP : géolocalisation actuelle uniquement (pas de saved zones — c'est
// une itération 2 qui demanderait une table HunterZone).

import React, { useState, useContext, useEffect, useCallback } from 'react';
import { LangContext } from '../App';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import ChasseHuntMap, { createDivIcon } from '../components/ChasseHuntMap';
import api from '../services/api';

const T = {
  fr: {
    title: 'Signalements près de chez vous',
    subtitle: 'Agriculteurs cherchant de l’aide pour gérer des dégâts dans votre rayon.',
    useGps: 'Utiliser ma position GPS',
    gettingGps: 'Récupération GPS…',
    radius: 'Rayon',
    search: 'Chercher',
    searching: 'Recherche…',
    empty: 'Aucun signalement actif dans ce rayon.',
    findings: (n, km) => `${n} signalement${n > 1 ? 's' : ''} dans un rayon de ${km} km`,
    species: 'Espèce',
    damage: 'Dégâts',
    description: 'Description',
    distance: 'Distance',
    opened: 'Ouvert',
    farmer: 'Agriculteur',
    contactHint:
      'Contactez le support pour être mis en relation avec l’agriculteur (workflow direct à venir).',
    gpsRequired: 'Géolocalisation requise pour chercher des signalements proches.',
    gpsFailed: 'GPS indisponible.',
    errGeneric: 'Erreur de chargement.',
  },
  en: {
    title: 'Reports near you',
    subtitle: 'Farmers looking for help with wildlife damage in your area.',
    useGps: 'Use my GPS position',
    gettingGps: 'Fetching GPS…',
    radius: 'Radius',
    search: 'Search',
    searching: 'Searching…',
    empty: 'No active reports in this radius.',
    findings: (n, km) => `${n} report${n > 1 ? 's' : ''} within ${km} km`,
    species: 'Species',
    damage: 'Damage',
    description: 'Description',
    distance: 'Distance',
    opened: 'Opened',
    farmer: 'Farmer',
    contactHint: 'Contact support to be matched with the farmer (direct workflow coming).',
    gpsRequired: 'Geolocation required to search for nearby reports.',
    gpsFailed: 'GPS unavailable.',
    errGeneric: 'Loading error.',
  },
};

const ICON_DAMAGE = createDivIcon({
  html: `<div style="
    width:28px;height:28px;border-radius:50% 50% 50% 0;
    background:#dc2626;border:2px solid #fff;
    transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.4);
    display:flex;align-items:center;justify-content:center;">
    <span style="transform:rotate(45deg);font-size:13px;line-height:1">⚠</span>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
});

const ICON_ME = createDivIcon({
  html: `<div style="width:16px;height:16px;background:#4a90d9;border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 5px rgba(74,144,217,0.2)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

export default function HuntersMatches() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;

  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [radius, setRadius] = useState(50);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gettingGps, setGettingGps] = useState(false);
  const [error, setError] = useState('');

  const getGps = useCallback(() => {
    if (!navigator.geolocation) {
      setError(t.gpsFailed);
      return;
    }
    setGettingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        setGettingGps(false);
        setError('');
      },
      () => {
        setGettingGps(false);
        setError(t.gpsFailed);
      },
      { enableHighAccuracy: true, timeout: 10_000 }
    );
  }, [t.gpsFailed]);

  const search = useCallback(async () => {
    if (lat == null || lng == null) {
      setError(t.gpsRequired);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/depredation/matches', {
        params: { lat, lng, radius_km: radius },
      });
      setReports(r.data.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || t.errGeneric);
    } finally {
      setLoading(false);
    }
  }, [lat, lng, radius, t]);

  // Auto-search dès qu'on a la GPS
  useEffect(() => {
    if (lat != null && lng != null) search();
  }, [lat, lng, search]);

  const markers = [
    ...reports.map((r) => ({
      id: r.id,
      lat: r.lat,
      lng: r.lng,
      icon: ICON_DAMAGE,
      popup: (
        <div style={{ minWidth: 220, fontSize: '0.85rem' }}>
          <strong style={{ fontSize: '0.95rem' }}>
            ⚠ {r.species} — {r.damage_type}
          </strong>
          <div style={{ marginTop: 6, color: '#444' }}>{r.description}</div>
          <div style={{ marginTop: 6, color: '#666', fontSize: '0.78rem' }}>
            {t.farmer} : {r.farmer_name}
          </div>
          <div style={{ color: '#666', fontSize: '0.78rem' }}>
            {t.distance} : ~{r.distance_km} km
          </div>
        </div>
      ),
    })),
    ...(lat != null && lng != null ? [{ id: 'me', lat, lng, icon: ICON_ME }] : []),
  ];

  return (
    <PrivateLayout>
      <main style={{ maxWidth: 1000, margin: '0 auto', padding: '2rem' }}>
        <PageHeader icon="🎯" title={t.title} subtitle={t.subtitle} />

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            alignItems: 'center',
            marginTop: '1.5rem',
            marginBottom: '1rem',
          }}
        >
          <button type="button" onClick={getGps} disabled={gettingGps} style={btnSecondary}>
            📍 {gettingGps ? t.gettingGps : t.useGps}
          </button>
          {lat != null && (
            <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)' }}>
              {lat.toFixed(4)}, {lng.toFixed(4)}
            </span>
          )}
          <label
            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <span style={{ fontSize: '0.85rem' }}>{t.radius} :</span>
            <select
              value={radius}
              onChange={(e) => setRadius(parseInt(e.target.value, 10))}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 4,
                color: 'var(--couleur-texte)',
                padding: '0.4rem 0.6rem',
              }}
            >
              <option value={10}>10 km</option>
              <option value={25}>25 km</option>
              <option value={50}>50 km</option>
              <option value={100}>100 km</option>
              <option value={200}>200 km</option>
            </select>
          </label>
          <button onClick={search} disabled={loading || lat == null} style={btnPrimary}>
            {loading ? t.searching : t.search}
          </button>
        </div>

        {error && <div style={{ color: '#ff6b6b', marginBottom: '1rem' }}>{error}</div>}

        {lat != null && (
          <>
            <div
              style={{
                marginBottom: '0.75rem',
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.9rem',
              }}
            >
              {t.findings(reports.length, radius)}
            </div>
            <div style={{ borderRadius: 10, overflow: 'hidden', marginBottom: '1.5rem' }}>
              <ChasseHuntMap
                center={[lat, lng]}
                zoom={9}
                basemap="osm"
                height="420px"
                markers={markers}
                fitToMarkers={reports.length > 0}
              />
            </div>

            {reports.length === 0 ? (
              <div
                style={{
                  color: 'rgba(255,255,255,0.5)',
                  padding: '2rem',
                  textAlign: 'center',
                }}
              >
                {t.empty}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {reports.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 8,
                      padding: '1rem',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                      }}
                    >
                      <strong>
                        ⚠ {r.species} — {r.damage_type}
                      </strong>
                      <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)' }}>
                        ~{r.distance_km} km
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: '0.4rem',
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '0.9rem',
                      }}
                    >
                      {r.description}
                    </div>
                    <div
                      style={{
                        marginTop: '0.4rem',
                        fontSize: '0.78rem',
                        color: 'rgba(255,255,255,0.5)',
                      }}
                    >
                      {t.farmer} : {r.farmer_name} · {t.opened}{' '}
                      {new Date(r.opened_at).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA')}
                    </div>
                  </div>
                ))}
                <div
                  style={{
                    fontSize: '0.82rem',
                    color: 'rgba(255,255,255,0.45)',
                    fontStyle: 'italic',
                    marginTop: '0.5rem',
                  }}
                >
                  ℹ {t.contactHint}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </PrivateLayout>
  );
}

const btnSecondary = {
  padding: '0.65rem 1rem',
  background: 'transparent',
  color: 'var(--couleur-or)',
  border: '1px solid var(--couleur-or)',
  borderRadius: '6px',
  fontWeight: 500,
  cursor: 'pointer',
};

const btnPrimary = {
  padding: '0.65rem 1.2rem',
  background: 'var(--couleur-or)',
  color: '#000',
  border: 'none',
  borderRadius: '6px',
  fontWeight: 600,
  cursor: 'pointer',
};
